
import React from 'react';
import { Plus, Trash2, GripVertical, FileText } from 'lucide-react';
import { WorkflowNodeData } from '../../../types';

interface FormField {
    id: string;
    label: string;
    key: string;
    type: string;
    required: boolean;
    placeholder?: string;
    options?: string; // Comma separated for select
}

interface FormBuilderTabProps {
    data: WorkflowNodeData;
    onChange: (key: string, value: any) => void;
}

export const FormBuilderTab: React.FC<FormBuilderTabProps> = ({ data, onChange }) => {
    // Form Schema Structure: { fields: FormField[] }
    const schema = data.formSchema || { fields: [] };
    const fields: FormField[] = schema.fields || [];

    const handleAddField = () => {
        const newField: FormField = {
            id: `field_${Date.now()}`,
            label: 'New Field',
            key: `field_${fields.length + 1}`,
            type: 'text',
            required: false
        };
        onChange('formSchema', { ...schema, fields: [...fields, newField] });
    };

    const handleRemoveField = (index: number) => {
        const newFields = fields.filter((_, i) => i !== index);
        onChange('formSchema', { ...schema, fields: newFields });
    };

    const handleFieldChange = (index: number, key: keyof FormField, value: any) => {
        const newFields = [...fields];
        newFields[index] = { ...newFields[index], [key]: value };
        onChange('formSchema', { ...schema, fields: newFields });
    };

    return (
        <div className="p-6 space-y-6">
            <div className="p-3 bg-teal-50 dark:bg-teal-900/20 rounded-lg border border-teal-100 dark:border-teal-900 flex gap-3">
                <FileText className="text-teal-600 dark:text-teal-400 mt-0.5 shrink-0" size={16} />
                <div className="space-y-1">
                    <p className="text-xs font-bold text-teal-800 dark:text-teal-300">Form Configuration</p>
                    <p className="text-[11px] text-teal-700 dark:text-teal-400 leading-relaxed">
                        Design the form interface for this task. These fields will be presented to the user during execution.
                    </p>
                </div>
            </div>

            <div className="space-y-4">
                {fields.map((field, index) => (
                    <div key={field.id} className="p-3 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-3">
                        <div className="flex items-center justify-between pb-2 border-b border-slate-100 dark:border-slate-800">
                             <div className="flex items-center gap-2">
                                <GripVertical size={14} className="text-slate-300 cursor-move" />
                                <span className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wide">Field #{index + 1}</span>
                             </div>
                             <button 
                                onClick={() => handleRemoveField(index)}
                                className="text-slate-400 hover:text-red-500 transition-colors"
                             >
                                <Trash2 size={14} />
                             </button>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Label</label>
                                <input 
                                    type="text"
                                    value={field.label}
                                    onChange={(e) => handleFieldChange(index, 'label', e.target.value)}
                                    className="w-full px-2 py-1.5 text-xs border rounded-lg bg-white border-slate-300 dark:border-slate-700 focus:ring-1 focus:ring-teal-500 focus:border-teal-500 outline-none"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Variable Key</label>
                                <input 
                                    type="text"
                                    value={field.key}
                                    onChange={(e) => handleFieldChange(index, 'key', e.target.value)}
                                    className="w-full px-2 py-1.5 text-xs border rounded-lg bg-white border-slate-300 dark:border-slate-700 focus:ring-1 focus:ring-teal-500 focus:border-teal-500 outline-none font-mono"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Type</label>
                                <input
                                    list={`type-options-${field.id}`}
                                    value={field.type}
                                    onChange={(e) => handleFieldChange(index, 'type', e.target.value)}
                                    className="w-full px-2 py-1.5 text-xs border rounded-lg bg-white border-slate-300 dark:border-slate-700 focus:ring-1 focus:ring-teal-500 focus:border-teal-500 outline-none"
                                />
                                <datalist id={`type-options-${field.id}`}>
                                    <option value="text">Text Input</option>
                                    <option value="textarea">Text Area</option>
                                    <option value="number">Number</option>
                                    <option value="boolean">Checkbox</option>
                                    <option value="date">Date</option>
                                    <option value="select">Dropdown</option>
                                    <option value="dialog">Dialog</option>
                                    <option value="email">Email</option>
                                    <option value="guid">GUID</option>
                                </datalist>
                            </div>
                            <div className="flex items-center pt-5">
                                <label className="flex items-center gap-2 cursor-pointer select-none">
                                    <input 
                                        type="checkbox"
                                        checked={field.required}
                                        onChange={(e) => handleFieldChange(index, 'required', e.target.checked)}
                                        className="rounded border-slate-300 text-teal-600 focus:ring-teal-500 w-3.5 h-3.5"
                                    />
                                    <span className="text-xs text-slate-600 dark:text-slate-400 font-medium">Required</span>
                                </label>
                            </div>
                        </div>

                        {field.type === 'select' && (
                            <div>
                                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Options (comma separated)</label>
                                <input 
                                    type="text"
                                    value={field.options || ''}
                                    onChange={(e) => handleFieldChange(index, 'options', e.target.value)}
                                    placeholder="Option 1, Option 2, Option 3"
                                    className="w-full px-2 py-1.5 text-xs border rounded-lg bg-white border-slate-300 dark:border-slate-700 focus:ring-1 focus:ring-teal-500 focus:border-teal-500 outline-none"
                                />
                            </div>
                        )}
                    </div>
                ))}

                <button 
                    onClick={handleAddField}
                    className="w-full py-2 border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-xl text-slate-500 hover:text-teal-600 hover:border-teal-400 hover:bg-teal-50 dark:hover:bg-slate-900 transition-all flex items-center justify-center gap-2 text-xs font-bold"
                >
                    <Plus size={14} /> Add Form Field
                </button>
            </div>
        </div>
    );
};
